import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Download, ExternalLink, Plus, Search } from 'lucide-react';
import { PageHeading } from '@/app/PageHeading';
import { errorMessage, isApiError } from '@/api/client';
import type { ProspectsOut, ProspectsRequest, PublisherLookupOut } from '@/api/types';
import { Alert } from '@/components/arc/alert/alert';
import { Button } from '@/components/arc/button/button';
import { EmptyState } from '@/components/arc/empty-state/empty-state';
import { NumberField } from '@/components/arc/number-field/number-field';
import SegmentedControl from '@/components/arc/segmented-control/segmented-control';
import { Skeleton } from '@/components/arc/skeleton/skeleton';
import { SortableDataTable, type DataColumn } from '@/components/arc/sortable-data-table/sortable-data-table';
import { Switch } from '@/components/arc/switch/switch';
import { TagInput } from '@/components/arc/tag-input/tag-input';
import { useApi } from '@/module/MomentProvider';
import { useHref } from '@/module/basePath';
import { fmtNum, fmtPct, normalizeDomain, PlayerBadge, ScoreBadge } from './PublisherQuality';
import overlay from './overlay.module.css';
import styles from './discovery.module.css';

type ProspectRow = { [K in keyof PublisherLookupOut]: PublisherLookupOut[K] };

const MAX_PUBLISHERS = 150;
const CSV_COLUMNS = [
  'domain', 'name', 'score', 'own_player', 'players', 'categories', 'a2cr', 'ads_in_view', 'page_weight',
  'supply_paths', 'resellers', 'owner_domain', 'reasons', 'sincera_url',
] as const;

/** The same columns as scripts/sincera_prospects.py, built in the browser. */
export function prospectsCsv(items: PublisherLookupOut[]): string {
  const cell = (v: unknown) => {
    const s = v == null ? '' : Array.isArray(v) ? v.join('; ') : typeof v === 'boolean' ? (v ? 'yes' : 'no') : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [CSV_COLUMNS.join(',')];
  for (const it of items) lines.push(CSV_COLUMNS.map((k) => cell(it[k])).join(','));
  return `${lines.join('\n')}\n`;
}

/** Publisher discovery: seed domains in, scored prospect sites out (OpenSincera data, cached by the API). */
export function DiscoveryPage() {
  const api = useApi();
  const href = useHref();
  const navigate = useNavigate();
  const [seeds, setSeeds] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [depth, setDepth] = useState<'1' | '2'>('1');
  const [max, setMax] = useState(50);
  const [ownOnly, setOwnOnly] = useState(true);
  const [request, setRequest] = useState<ProspectsRequest | null>(null);

  const search = useMutation({
    mutationFn: (body: ProspectsRequest) => api.publishers.prospects(body),
  });
  const result: ProspectsOut | undefined = search.data;
  const items = result?.items ?? [];
  const validSeeds = seeds.map(normalizeDomain).filter((d) => d.includes('.'));
  const valid = validSeeds.length > 0 && !search.isPending;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    const body: ProspectsRequest = {
      seeds: Array.from(new Set(validSeeds)).slice(0, 10),
      categories,
      depth: Number(depth),
      max_publishers: Math.min(Math.max(max, 1), MAX_PUBLISHERS),
      require_own_player: ownOnly,
    };
    setRequest(body);
    search.mutate(body);
  };

  const exportCsv = () => {
    const blob = new Blob([prospectsCsv(items)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `prospects-${(request?.seeds ?? ['sincera']).join('-').replace(/[^a-z0-9.-]+/gi, '_')}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const addAsPublisher = (row: PublisherLookupOut) => {
    navigate(href('/overlay/publishers'), {
      state: { addPublisherDomain: row.domain, addPublisherName: row.name ?? row.domain },
    });
  };

  const columns: DataColumn<ProspectRow>[] = [
    {
      key: 'domain',
      label: 'Site',
      render: (_, r) => (
        <div className={overlay.cell}>
          <span>{r.domain}</span>
          <span className={overlay.cellSub}>{r.name && r.name !== r.domain ? r.name : r.owner_domain ?? ''}</span>
        </div>
      ),
    },
    { key: 'score', label: 'Score', numeric: true, render: (_, r) => <ScoreBadge score={r.score} reasons={r.reasons} /> },
    {
      key: 'own_player',
      label: 'Players',
      render: (_, r) => (
        <span className={styles.players}>
          <PlayerBadge ownPlayer={r.own_player} players={r.players} />
          {r.players.length > 0 && <span className={overlay.cellSub}>{r.players.join(', ')}</span>}
        </span>
      ),
    },
    {
      key: 'categories',
      label: 'Categories',
      sortable: false,
      render: (_, r) => <span className={styles.wrap}>{r.categories.length ? r.categories.join(', ') : 'n/a'}</span>,
    },
    { key: 'a2cr', label: 'Ads to content', numeric: true, render: (v) => fmtPct(v as number | null) },
    { key: 'ads_in_view', label: 'Ads in view', numeric: true, render: (v) => fmtNum(v as number | null) },
    { key: 'supply_paths', label: 'Supply paths', numeric: true, render: (v) => (v == null ? 'n/a' : String(v)) },
    {
      key: 'sincera_url',
      label: 'Sincera',
      sortable: false,
      render: (v) =>
        v ? (
          <a href={String(v)} target="_blank" rel="noreferrer" className={styles.extLink}>
            Open <ExternalLink size={12} strokeWidth={1.75} aria-hidden="true" />
          </a>
        ) : (
          <span className="text-muted">n/a</span>
        ),
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      render: (_, r) => (
        <span className={overlay.rowActions}>
          <Button variant="ghost" size="sm" onClick={() => addAsPublisher(r)}>
            <Plus size={14} strokeWidth={1.75} aria-hidden="true" /> Add as publisher
          </Button>
        </span>
      ),
    },
  ];

  const errorStatus = search.error && isApiError(search.error) ? search.error.status : 0;

  return (
    <main className="page">
      <PageHeading
        title="Publisher discovery"
        description="Find sites that run their own video player, with ad density and categories from OpenSincera"
      />

      <form className={styles.form} onSubmit={submit} aria-label="Prospect search">
        <div className={styles.formGrid}>
          <TagInput
            label="Seed domains"
            value={seeds}
            onValueChange={setSeeds}
            placeholder="yemek.com"
            description="Press Enter after each domain. Similar sites are found from these."
          />
          <TagInput
            label="Categories"
            value={categories}
            onValueChange={setCategories}
            placeholder="Food & Drink"
            description="Optional. A matching category adds to the score."
          />
          <SegmentedControl
            label="How far to look"
            value={depth}
            onValueChange={(v) => setDepth(v as '1' | '2')}
            options={[
              { value: '1', label: 'Similar sites' },
              { value: '2', label: 'Similar of similar' },
            ]}
          />
          <NumberField
            label="Max results"
            value={max}
            onValueChange={setMax}
            min={1}
            max={MAX_PUBLISHERS}
            step={10}
            description={`Up to ${MAX_PUBLISHERS} sites per search`}
          />
        </div>
        <div className={styles.formFoot}>
          <Switch label="Require own player" checked={ownOnly} onCheckedChange={setOwnOnly} />
          <Button type="submit" loading={search.isPending} disabled={!valid}>
            <Search size={16} strokeWidth={1.75} aria-hidden="true" /> Find publishers
          </Button>
        </div>
      </form>

      {search.isPending ? (
        <section className="stack-4" aria-live="polite">
          <p className={overlay.muted}>
            Walking similar sites. A full search of {MAX_PUBLISHERS} sites takes about a minute; results are kept for a day.
          </p>
          <Skeleton lines={6} label="Searching publishers" />
        </section>
      ) : search.isError ? (
        <Alert tone="danger" title={errorStatus === 503 ? 'Publisher discovery is not configured' : 'The search failed'}>
          {errorMessage(search.error)}{' '}
          {errorStatus !== 503 && request && (
            <Button variant="secondary" size="sm" onClick={() => search.mutate(request)}>
              Try again
            </Button>
          )}
        </Alert>
      ) : !result ? (
        <EmptyState
          title="Start from a site you know"
          description="Add one or more seed domains and Scenewise walks their similar sites, scoring each for an own video player, category match and ad density."
        />
      ) : items.length === 0 ? (
        <EmptyState
          title="No sites matched"
          description={ownOnly ? 'Turn off "Require own player" or add other seed domains.' : 'Try other seed domains.'}
        />
      ) : (
        <section className="stack-4" aria-labelledby="prospects-heading">
          <div className={overlay.sectionHead}>
            <div className={overlay.cell}>
              <h2 id="prospects-heading" className={overlay.sectionTitle}>
                Prospects
              </h2>
              <span className={overlay.sectionMeta}>
                {items.length} sites, {items.filter((i) => i.own_player).length} with their own player.
                {result.cached ? ' From the daily cache.' : ` ${result.api_calls} lookups.`}
              </span>
            </div>
            <Button variant="secondary" size="sm" onClick={exportCsv}>
              <Download size={16} strokeWidth={1.75} aria-hidden="true" /> Export CSV
            </Button>
          </div>
          <div className="table-scroll">
            <SortableDataTable<ProspectRow>
              rows={items}
              columns={columns}
              rowKey="domain"
              caption="Publisher prospects"
              defaultSort={{ key: 'score', direction: 'desc' }}
              itemName={{ one: 'site', other: 'sites' }}
            />
          </div>
        </section>
      )}
    </main>
  );
}
